import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { WModal } from 'shared_components/components/WModal/WModal';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { WCheckBox } from 'shared_components/components/WCheckBox/WCheckBox';
import {
  ContributionDetailModal,
  type ContributionAliasOption,
} from './ContributionDetailModal';
import { ContributionChip, ValueChip } from './manualChangeChip';
import { ChangeReleaseModal } from './ChangeReleaseModal';
import {
  ArtistEditorModal,
  type ArtistEntry,
  type FeaturedStatus,
} from './ArtistEditorModal';
import {
  ArtistSearchDropdown,
  FeaturedStatusModal,
  type SelectedArtist,
} from './artistEditing';
import type { Row } from '../MasterDiscographyTypes';
import { useApi } from 'shared_components/context';
import { countryCodes } from 'shared_components/utils/Countries';
import {
  extractYear,
  findDuplicate,
  validateRecordingYear,
} from '../../../utils/recordingValidators';
import {
  FIELDS,
  FIELD_OPERATIONS,
  availableOperations,
  bulkEditConflicts,
  editorKindFor,
  fieldIsExhausted,
  rowIsComplete,
  type BulkEditField,
  type BulkEditOperation,
  type BulkEditRow,
  type ConfiguredContribution,
  type RowValue,
} from '../bulkEditModel';
import {
  applyBulkEdits,
  buildBulkEditPlan,
  computeRuleViolations,
  type BulkEditApplyResult,
  type BulkEditTarget,
  type RuleViolation,
} from '../bulkEditBatch';
import { parseEvidenceNotes } from '../massEditBatch';
import {
  FIELD_ERROR_BORDER_CLASS,
  FIELD_ERROR_CLASS,
  FIELD_HELP_CLASS,
  FIELD_HINT_CLASS,
  FIELD_INPUT_CLASS,
  FIELD_LABEL_CLASS,
  MODAL_ACTIONS_CLASS,
  SearchSelect,
  fieldControlClass,
} from './formStyles';

/**
 * Bulk edit modal.
 *
 * A plan builder: the user assembles a list of edit rows, each a
 * field / operation / value combination, then applies them to every selected
 * recording in one call. The value for each row is configured through a
 * secondary modal appropriate to the field & operation (reusing the recording
 * edit form's modals where possible), so the same control edits one recording
 * or a thousand.
 *
 * The row shapes and the rules for what makes a set of them coherent live in
 * `../bulkEditModel`; turning them into the wire payload lives in
 * `../bulkEditBatch`. This file is the surface.
 */

const capitalize = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

/** Searchable role dropdown. The same control, from the same module, as the
 *  one the recording edit form and the mass-edit modals use. */
const RoleSelect: React.FC<{
  options: string[];
  exclude?: string[];
  onSelect: (role: string) => void;
  placeholder?: string;
}> = (props) => <SearchSelect {...props} renderOption={capitalize} />;

/** A simple checklist used by the delete operations. */
const CheckList: React.FC<{
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}> = ({ options, selected, onToggle }) =>
  options.length === 0 ? (
    <p className={FIELD_HELP_CLASS}>
      Nothing is currently assigned across the selected recordings.
    </p>
  ) : (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-max-h-64 tw-overflow-y-auto">
      {options.map((opt) => (
        <WCheckBox
          key={opt}
          checkboxSize="sm"
          checked={selected.includes(opt)}
          onChange={() => onToggle(opt)}
          label={capitalize(opt)}
        />
      ))}
    </div>
  );

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The recordings the plan applies to. */
  targets: BulkEditTarget[];
  /** The same recordings as grid rows, for the recording-before-release
   *  pre-warning (each row's `year` and `yearRecorded`) and for naming
   *  recordings the backend rejects. Optional: without it the warning is
   *  skipped and rejections are reported by id. */
  selectedRows?: Row[];
  availableContributions?: string[];
  availableArtists?: string[];
  availableAliases?: ContributionAliasOption[];
  /** Contribution roles currently assigned across the selected recordings. */
  assignedContributions?: string[];
  /** Artists currently assigned across the selected recordings. */
  assignedArtists?: string[];
  /** Artist display name → graph id, so the name-based Delete / Demote
   *  checklists can send an id rather than a display string. */
  artistIdByName?: Record<string, string>;
  /** Context for the shared Change Release modal (lookups need a recording). */
  userId?: string;
  releaseContextRow?: Row | null;
  availableFormats?: string[];
  availableTypes?: string[];
  /** Called after an apply in which at least one action landed, so the caller
   *  can refetch. `ok` is false on a partial success. */
  onApplied?: (result: BulkEditApplyResult) => void;
}

let ROW_SEQ = 0;
const newRow = (): BulkEditRow => ({
  id: `row-${ROW_SEQ++}`,
  field: '',
  operation: '',
  value: null,
});

export const BulkEditModal: React.FC<BulkEditModalProps> = ({
  isOpen,
  onClose,
  targets,
  selectedRows = [],
  availableContributions = [],
  availableArtists = [],
  availableAliases = [],
  assignedContributions = [],
  assignedArtists = [],
  artistIdByName = {},
  userId,
  releaseContextRow = null,
  availableFormats = [],
  availableTypes = [],
  onApplied,
}) => {
  const { adminApi } = useApi();
  const selectedCount = targets.length;
  const [rows, setRows] = useState<BulkEditRow[]>([newRow()]);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<BulkEditApplyResult | null>(null);

  // Reset to a single empty row whenever the modal is (re)opened.
  useEffect(() => {
    if (isOpen) {
      setRows([newRow()]);
      setEditingRowId(null);
      setApplying(false);
      setResult(null);
    }
  }, [isOpen]);

  const contributionOptions = useMemo(
    () => Array.from(new Set(availableContributions.filter(Boolean))).sort(),
    [availableContributions]
  );

  const updateRow = (id: string, patch: Partial<BulkEditRow>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const handleFieldChange = (id: string, field: BulkEditField | '') => {
    if (!field) {
      updateRow(id, { field: '', operation: '', value: null });
      return;
    }
    // The operation is always the user's own pick, even when the other rows
    // have left only one open (`availableOperations`). Pre-selecting that one
    // and locking the control read as "this row can't be changed" in UAT —
    // an unexplained, greyed-out "Delete" — so the dropdown stays live and
    // shows the taken operations disabled instead.
    updateRow(id, { field, operation: '', value: null });
  };

  const handleOperationChange = (
    id: string,
    operation: BulkEditOperation | ''
  ) => updateRow(id, { operation, value: null });

  const editingRow = rows.find((r) => r.id === editingRowId) ?? null;

  const rowSummary = (row: BulkEditRow): string => {
    if (!row.value) return '';
    switch (row.value.kind) {
      case 'title':
        return row.value.title ? `“${row.value.title}”` : '';
      case 'live':
        return row.value.descriptor
          ? `Live · “${row.value.descriptor}”`
          : 'Mark as live';
      case 'contributions': {
        return row.value.entries
          .map((e) => capitalize(e.contribution))
          .join(', ');
      }
      case 'contributionDelete':
        return row.value.roles.map(capitalize).join(', ');
      case 'artist':
        return row.value.entries.map((e) => e.name).join(', ');
      case 'artistDelete':
        return row.value.artists.map(capitalize).join(', ');
      case 'artistDemote':
        return row.value.artists.map(capitalize).join(', ');
      case 'release':
        return row.value.label ? `→ ${row.value.label}` : '';
      case 'yearRecorded':
        return row.value.year;
      case 'country': {
        const { code } = row.value;
        return countryCodes.find((c) => c.code === code)?.name ?? code;
      }
      case 'pName':
        return row.value.names.join(', ');
      default:
        return '';
    }
  };

  const readyRows = rows.filter(rowIsComplete);
  // Contradictions between rows, surfaced before Apply rather than as a 400:
  // the request carries one bucket per field, so two rows that disagree would
  // arrive as two instructions and one would silently lose.
  const conflicts = useMemo(() => bulkEditConflicts(rows), [rows]);

  // Recordings the backend would refuse under the recording-before-release
  // rule (spec §3), worked out from the grid's own data before anything is
  // sent. Enforced as a validation error: Apply stays disabled until the year
  // is changed or the offending recordings are deselected, so a bulk edit
  // never lands on part of the selection. (The backend still checks and
  // reports `rejected` as a backstop for stale grid data.)
  const ruleViolations = useMemo<RuleViolation[]>(() => {
    if (conflicts.length > 0 || selectedRows.length === 0) return [];
    const { plan } = buildBulkEditPlan(rows, targets, { artistIdByName });
    return computeRuleViolations(selectedRows, plan);
  }, [rows, targets, selectedRows, artistIdByName, conflicts]);

  // (P) names present across the selected recordings — the Delete editor
  // offers these, like contributions and artists do.
  const assignedPNames = useMemo(
    () => selectedRows.flatMap((r) => r.pNames ?? []),
    [selectedRows]
  );

  const titleByRecordingId = useMemo(
    () =>
      Object.fromEntries(selectedRows.map((r) => [r.id, r.title])) as Record<
        string,
        string
      >,
    [selectedRows]
  );

  const handleApply = async () => {
    if (!userId) return;
    setApplying(true);
    setResult(null);
    try {
      const applied = await applyBulkEdits(adminApi, userId, rows, targets, {
        artistIdByName,
        titleByRecordingId,
      });
      setResult(applied);
      // A partial success still changed data, so the grid needs refetching
      // either way — only a fatal error left everything untouched.
      if (!applied.fatalError) onApplied?.(applied);
      if (applied.ok) onClose();
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      {/* Keep the builder mounted while a row editor is open so the editor
          stacks on top (its backdrop dims the builder underneath). */}
      <WModal
        isOpen={isOpen}
        onClose={onClose}
        title={`Bulk edit ${selectedCount} recording${
          selectedCount === 1 ? '' : 's'
        }`}
        modalClasses="tw-w-[980px] tw-max-w-[97vw]"
      >
        <div className="tw-flex tw-flex-col tw-gap-4">
          {/* Heading + add-row CTA */}
          <div className="tw-flex tw-items-center tw-justify-between">
            <span className={FIELD_LABEL_CLASS}>
              Build a set of edits to apply to the selected recordings.
            </span>
            <WButton
              variant="secondary"
              icon="plus"
              label="Add row"
              onClick={() => setRows((prev) => [...prev, newRow()])}
            />
          </div>

          {/* Edits table */}
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-text-sm">
              <thead>
                <tr className="tw-text-left tw-text-textDove tw-border-b tw-border-formInputBackground">
                  <th className="tw-py-2 tw-pr-3 tw-font-medium tw-w-[200px]">
                    Field
                  </th>
                  <th className="tw-py-2 tw-pr-3 tw-font-medium tw-w-[200px]">
                    Operation
                  </th>
                  <th className="tw-py-2 tw-pr-3 tw-font-medium">Values</th>
                  <th className="tw-py-2 tw-w-[40px]" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  // Every operation is listed, but the ones another row has
                  // already claimed are greyed out rather than hidden, so the
                  // "only one of Add / Edit / Replace" rule is visible where it
                  // applies instead of options silently disappearing. That
                  // holds even when a single operation is left: the control
                  // stays enabled so the user can open it and see *why* the
                  // rest are unavailable, rather than meeting a locked select.
                  const ops = row.field ? FIELD_OPERATIONS[row.field] : [];
                  const selectableOps = row.field
                    ? availableOperations(row.field, rows, row.id)
                    : [];
                  const opDisabled = !row.field;
                  const canEdit = !!row.field && !!row.operation;
                  return (
                    <tr
                      key={row.id}
                      className="tw-border-b tw-border-formInputBackground tw-align-top"
                    >
                      {/* Field */}
                      <td className="tw-py-2 tw-pr-3">
                        <select
                          className={fieldControlClass({
                            kind: 'select',
                            hasValue: !!row.field,
                          })}
                          value={row.field}
                          onChange={(e) =>
                            handleFieldChange(
                              row.id,
                              e.target.value as BulkEditField | ''
                            )
                          }
                        >
                          <option value="">Select field…</option>
                          {FIELDS.map((f) => {
                            const exhausted = fieldIsExhausted(f, rows, row.id);
                            return (
                              <option key={f} value={f} disabled={exhausted}>
                                {exhausted ? `${f} (already in this edit)` : f}
                              </option>
                            );
                          })}
                        </select>
                      </td>
                      {/* Operation */}
                      <td className="tw-py-2 tw-pr-3">
                        <select
                          className={fieldControlClass({
                            kind: 'select',
                            hasValue: !!row.operation,
                          })}
                          value={row.operation}
                          disabled={opDisabled}
                          onChange={(e) =>
                            handleOperationChange(
                              row.id,
                              e.target.value as BulkEditOperation | ''
                            )
                          }
                        >
                          <option value="">Select operation…</option>
                          {ops.map((op) => (
                            <option
                              key={op}
                              value={op}
                              disabled={!selectableOps.includes(op)}
                            >
                              {op}
                            </option>
                          ))}
                        </select>
                      </td>
                      {/* Values */}
                      <td className="tw-py-2 tw-pr-3">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <span
                            className={clsx(
                              'tw-flex-1 tw-min-h-[44px] tw-flex tw-items-center tw-rounded-lg tw-border-2 tw-border-formInputBackground tw-bg-formInputBackground tw-px-3 tw-text-sm',
                              rowSummary(row)
                                ? 'tw-text-white'
                                : 'tw-text-textSeal'
                            )}
                          >
                            {rowSummary(row) || 'No value set'}
                          </span>
                          <WButton
                            variant="secondary"
                            icon="edit"
                            label="Edit"
                            disabled={!canEdit}
                            onClick={() => setEditingRowId(row.id)}
                          />
                        </div>
                      </td>
                      {/* Remove row */}
                      <td className="tw-py-2 tw-text-right">
                        <button
                          type="button"
                          aria-label="Remove row"
                          className="tw-text-textDove hover:tw-text-white tw-p-1"
                          onClick={() =>
                            setRows((prev) =>
                              prev.length === 1
                                ? [newRow()]
                                : prev.filter((r) => r.id !== row.id)
                            )
                          }
                        >
                          <X className="tw-w-4 tw-h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Contradictions between rows — blocking, and fixable by the user. */}
          {conflicts.length > 0 && (
            <div className="tw-rounded-xl tw-border tw-border-yellow-700/60 tw-bg-yellow-900/20 tw-p-3">
              <ul className="tw-flex tw-flex-col tw-gap-1">
                {conflicts.map((problem) => (
                  <li key={problem} className="tw-text-xs tw-text-yellow-200">
                    {problem}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recording-before-release violations — a validation error that
              blocks Apply until resolved. */}
          {!result && ruleViolations.length > 0 && (
            <RuleViolationPanel
              violations={ruleViolations}
              selectedCount={selectedCount}
            />
          )}

          {/* Outcome. Each action is applied by a single set-based query, so
              the result is reported per action, not per recording — a failed
              action means that part of the plan didn't land, for all of them.
              The one per-recording signal is `rejected`: recordings a rule
              refused inside an action that otherwise went ahead. */}
          {result && <BulkEditResult result={result} />}

          {/* Footer */}
          <div className="tw-flex tw-justify-end tw-gap-3 tw-pt-2">
            <WButton
              variant="secondary"
              label={result && !result.ok ? 'Close' : 'Cancel'}
              onClick={onClose}
            />
            <WButton
              variant="primary"
              label={
                applying
                  ? 'Applying…'
                  : `${result && !result.ok ? 'Retry' : 'Apply'} ${
                      readyRows.length
                    } edit${
                      readyRows.length === 1 ? '' : 's'
                    } to ${selectedCount} recording${
                      selectedCount === 1 ? '' : 's'
                    }`
              }
              disabled={
                readyRows.length === 0 ||
                selectedCount === 0 ||
                conflicts.length > 0 ||
                ruleViolations.length > 0 ||
                applying ||
                !userId
              }
              onClick={handleApply}
            />
          </div>
        </div>
      </WModal>

      {/* Secondary value editor for the row being edited. */}
      {editingRow && editingRow.field && editingRow.operation && (
        <RowValueEditor
          row={editingRow}
          contributionOptions={contributionOptions}
          availableArtists={availableArtists}
          availableAliases={availableAliases}
          assignedContributions={assignedContributions}
          assignedArtists={assignedArtists}
          assignedPNames={assignedPNames}
          userId={userId}
          releaseContextRow={releaseContextRow}
          bulkRecordingIds={targets.map((t) => t.recordingId)}
          availableFormats={availableFormats}
          availableTypes={availableTypes}
          onClose={() => setEditingRowId(null)}
          onSave={(value) => {
            updateRow(editingRow.id, { value });
            setEditingRowId(null);
          }}
        />
      )}
    </>
  );
};

/**
 * “N of M selected recordings break the recording-before-release rule”, with
 * the offenders behind a disclosure so a long list doesn't push the Apply
 * button off screen. Red like the result errors: it's a validation error, and
 * Apply stays disabled while it shows. Each offender is named by title and
 * version so recordings sharing a title can be told apart.
 */
const RuleViolationPanel: React.FC<{
  violations: RuleViolation[];
  selectedCount: number;
}> = ({ violations, selectedCount }) => {
  const [expanded, setExpanded] = useState(true);
  const n = violations.length;
  return (
    <div
      role="alert"
      className="tw-rounded-xl tw-border tw-border-red-700/60 tw-bg-red-900/20 tw-p-3 tw-flex tw-flex-col tw-gap-2"
    >
      <button
        type="button"
        className="tw-flex tw-items-center tw-gap-2 tw-text-left tw-text-xs tw-text-red-200"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? (
          <ChevronDown className="tw-w-4 tw-h-4 tw-shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="tw-w-4 tw-h-4 tw-shrink-0" aria-hidden />
        )}
        <span>
          {n} of {selectedCount} selected recording
          {selectedCount === 1 ? '' : 's'} break the recording-before-release
          rule. Change the Year of Recording or deselect them before applying.
        </span>
      </button>
      {expanded && (
        <ul className="tw-flex tw-flex-col tw-gap-1 tw-pl-6 tw-max-h-48 tw-overflow-y-auto">
          {violations.map((v) => (
            <li key={v.recordingId} className="tw-text-xs tw-text-red-100">
              <span className="tw-text-white">“{v.title || '(untitled)'}”</span>
              {v.version && (
                <span className="tw-text-red-100"> ({v.version})</span>
              )}
              <span className="tw-text-red-200">
                {' '}
                — Year of Recording {v.yearRecorded}, on{' '}
                {v.releaseTitle ? `“${v.releaseTitle}”` : 'the linked release'}{' '}
                released {extractYear(v.releaseYear) ?? v.releaseYear}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * What landed and what didn't.
 *
 * Reported per action rather than per recording because that's all the contract
 * can tell us: DS runs one set-based query per action, so a failure covers the
 * whole selection for that action and says nothing about individual recordings
 * (contract §5). Retrying is resubmitting the same plan — the apply is
 * idempotent, so the actions that already succeeded are harmless to replay.
 */
const BulkEditResult: React.FC<{ result: BulkEditApplyResult }> = ({
  result,
}) => {
  if (result.fatalError) {
    return (
      <div className="tw-rounded-xl tw-border tw-border-red-700/60 tw-bg-red-900/20 tw-p-3">
        <p className="tw-text-xs tw-text-red-200">{result.fatalError}</p>
        <p className="tw-text-xs tw-text-red-300 tw-mt-1">
          Nothing was applied.
        </p>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'tw-rounded-xl tw-border tw-p-3 tw-flex tw-flex-col tw-gap-2',
        result.ok
          ? 'tw-border-formInputBackground tw-bg-surfaceBlack80'
          : 'tw-border-red-700/60 tw-bg-red-900/20'
      )}
    >
      {!result.ok && (
        <p className="tw-text-xs tw-text-red-200">
          Some of this edit could not be applied. Fixing the cause and applying
          again is safe — the parts that already succeeded will simply be
          reapplied.
        </p>
      )}
      {result.outcomes.map((outcome) => (
        <div key={outcome.action} className="tw-flex tw-flex-col tw-gap-1">
          <div className="tw-flex tw-items-start tw-gap-2 tw-text-xs">
            <span
              className={outcome.ok ? 'tw-text-textLime' : 'tw-text-red-300'}
              aria-hidden
            >
              {outcome.ok ? '✓' : '✕'}
            </span>
            <span className="tw-text-white">{outcome.label}</span>
            {!outcome.ok && outcome.message && (
              <span className="tw-text-red-300">— {outcome.message}</span>
            )}
          </div>
          {/* Per-recording refusals inside this action, e.g. the
              recording-before-release rule (spec §3). */}
          {outcome.rejected.length > 0 && (
            <div className="tw-pl-5 tw-flex tw-flex-col tw-gap-1">
              <p className="tw-text-xs tw-text-red-200">
                {outcome.rejected.length} recording
                {outcome.rejected.length === 1 ? '' : 's'} rejected:
              </p>
              <ul className="tw-flex tw-flex-col tw-gap-1 tw-max-h-48 tw-overflow-y-auto">
                {outcome.rejected.map((r) => (
                  <li key={r.recordingId} className="tw-text-xs">
                    <span className="tw-text-white">{r.title}</span>
                    <span className="tw-text-red-300"> — {r.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------------- */
/* Secondary value editors                                                   */
/* ------------------------------------------------------------------------- */

const RowValueEditor: React.FC<{
  row: BulkEditRow;
  contributionOptions: string[];
  availableArtists: string[];
  availableAliases: ContributionAliasOption[];
  assignedContributions: string[];
  assignedArtists: string[];
  /** (P) names currently carried by the selected recordings (Delete options). */
  assignedPNames: string[];
  userId?: string;
  releaseContextRow: Row | null;
  bulkRecordingIds: string[];
  availableFormats: string[];
  availableTypes: string[];
  onClose: () => void;
  onSave: (value: RowValue) => void;
}> = ({
  row,
  contributionOptions,
  availableArtists,
  availableAliases,
  assignedContributions,
  assignedArtists,
  assignedPNames,
  userId,
  releaseContextRow,
  bulkRecordingIds,
  availableFormats,
  availableTypes,
  onClose,
  onSave,
}) => {
  const kind = editorKindFor(
    row.field as BulkEditField,
    row.operation as BulkEditOperation
  );

  switch (kind) {
    case 'title':
      return (
        <TextValueEditor
          title={`${row.operation} recording title`}
          description="This title will be applied to the selected recordings."
          label="Recording title"
          placeholder="Title to apply"
          initial={row.value?.kind === 'title' ? row.value.title : ''}
          onClose={onClose}
          onSave={(title) => onSave({ kind: 'title', title })}
        />
      );
    case 'live':
      return (
        <TextValueEditor
          title={
            row.value?.kind === 'live' && row.value.descriptor.trim().length > 0
              ? 'Update live version'
              : 'Mark as live'
          }
          description="The selected recordings will be marked as live. A live descriptor is required, as on the recording edit form. Recordings whose live descriptor was set automatically and never manually changed will be skipped."
          label="Live descriptor"
          placeholder="e.g. Live at Wembley, 1985"
          required
          initial={row.value?.kind === 'live' ? row.value.descriptor : ''}
          onClose={onClose}
          onSave={(descriptor) =>
            onSave({ kind: 'live', descriptor: descriptor.trim() })
          }
        />
      );
    case 'release':
      return (
        <ChangeReleaseModal
          isOpen
          mode="select"
          userId={userId}
          row={releaseContextRow}
          // The lookup is about the whole selection, not the one recording that
          // happens to supply the display context.
          bulkRecordingIds={bulkRecordingIds}
          availableFormats={availableFormats}
          availableTypes={availableTypes}
          onClose={onClose}
          onApplied={(updatedRow, selection) =>
            onSave({
              kind: 'release',
              label: updatedRow.release,
              // The linkage — an existing release id, or the metadata to create
              // one from. Without it the row can only be displayed, not applied.
              selection,
              release: updatedRow.release,
              earliestParentReleaseId: updatedRow.earliestParentReleaseId,
              sourceReleaseId: updatedRow.sourceReleaseId,
              year: updatedRow.year,
              format: updatedRow.format,
              mediaType: updatedRow.mediaType,
              barcode: updatedRow.barcode,
            })
          }
        />
      );
    case 'yearRecorded':
      return (
        <YearValueEditor
          initial={row.value?.kind === 'yearRecorded' ? row.value.year : ''}
          onClose={onClose}
          onSave={(year) => onSave({ kind: 'yearRecorded', year })}
        />
      );
    case 'country':
      return (
        <CountryValueEditor
          initial={row.value?.kind === 'country' ? row.value.code : ''}
          onClose={onClose}
          onSave={(code) => onSave({ kind: 'country', code })}
        />
      );
    case 'pName':
      return row.operation === 'Delete' ? (
        <DeleteListEditor
          title="Delete (P) names"
          description="Select (P) names to remove. Only names currently carried by the selected recordings are shown; recordings without a selected name are left unchanged."
          options={assignedPNames}
          initial={row.value?.kind === 'pName' ? row.value.names : []}
          onClose={onClose}
          onSave={(names) => onSave({ kind: 'pName', names })}
        />
      ) : (
        <PNameListEditor
          initial={row.value?.kind === 'pName' ? row.value.names : []}
          onClose={onClose}
          onSave={(names) => onSave({ kind: 'pName', names })}
        />
      );
    case 'artistDemote':
      return (
        <DeleteListEditor
          title="Demote artists to non-featured"
          description="Select artists to demote to non-featured. Only artists currently assigned across the selected recordings are shown. As on the edit form, demote keeps the artist linked but non-featured (use Delete to remove them entirely)."
          options={assignedArtists}
          initial={row.value?.kind === 'artistDemote' ? row.value.artists : []}
          onClose={onClose}
          onSave={(artists) => onSave({ kind: 'artistDemote', artists })}
        />
      );
    case 'contributionDelete':
      return (
        <DeleteListEditor
          title="Delete contributions"
          description="Select contributions to remove. Only contributions currently assigned across the selected recordings are shown."
          options={assignedContributions}
          initial={
            row.value?.kind === 'contributionDelete' ? row.value.roles : []
          }
          onClose={onClose}
          onSave={(roles) => onSave({ kind: 'contributionDelete', roles })}
        />
      );
    case 'artistDelete':
      return (
        <DeleteListEditor
          title="Delete artists"
          description="Select artists to remove. Only artists currently assigned across the selected recordings are shown. A recording must keep at least one artist, so on apply this removal is skipped for any recording where it would remove the last remaining artist."
          options={assignedArtists}
          initial={row.value?.kind === 'artistDelete' ? row.value.artists : []}
          onClose={onClose}
          onSave={(artists) => onSave({ kind: 'artistDelete', artists })}
        />
      );
    case 'contributions':
      return (
        <ContributionListEditor
          operation={row.operation as 'Add' | 'Edit' | 'Replace'}
          contributionOptions={contributionOptions}
          assignedContributions={assignedContributions}
          availableAliases={availableAliases}
          initial={row.value?.kind === 'contributions' ? row.value.entries : []}
          onClose={onClose}
          onSave={(entries) => onSave({ kind: 'contributions', entries })}
        />
      );
    case 'artist':
      return (
        <ArtistAddEditor
          replaceAll={row.operation === 'Replace'}
          availableArtists={availableArtists}
          userId={userId}
          parentRecordingId={releaseContextRow?.id}
          initial={row.value?.kind === 'artist' ? row.value.entries : []}
          onClose={onClose}
          onSave={(entries) => onSave({ kind: 'artist', entries })}
        />
      );
    default:
      return null;
  }
};

/** Simple single-input editor used for Recording Title and Live. */
const TextValueEditor: React.FC<{
  title: string;
  description: string;
  label: string;
  placeholder: string;
  required?: boolean;
  initial: string;
  onClose: () => void;
  onSave: (value: string) => void;
}> = ({
  title,
  description,
  label,
  placeholder,
  required = true,
  initial,
  onClose,
  onSave,
}) => {
  const [value, setValue] = useState(initial);
  return (
    <WModal
      isOpen
      onClose={onClose}
      title={title}
      modalClasses="tw-w-[520px] tw-max-w-[95vw]"
    >
      <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
        <p className={FIELD_HELP_CLASS}>{description}</p>
        <div className="tw-flex tw-flex-col tw-gap-2">
          <span className={FIELD_LABEL_CLASS}>{label}</span>
          <input
            className={FIELD_INPUT_CLASS}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
          />
        </div>
        <div className={MODAL_ACTIONS_CLASS}>
          <WButton variant="secondary" label="Cancel" onClick={onClose} />
          <WButton
            variant="primary"
            label="Save"
            disabled={required && value.trim().length === 0}
            onClick={() => onSave(value)}
          />
        </div>
      </div>
    </WModal>
  );
};

/**
 * (P) Name Add: a free-text chip list, so several names can be added in one
 * row (mirrors the mass-edit (P) Name modal and the ISRC editor).
 */
const PNameListEditor: React.FC<{
  initial: string[];
  onClose: () => void;
  onSave: (names: string[]) => void;
}> = ({ initial, onClose, onSave }) => {
  const [names, setNames] = useState<string[]>(initial);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addDraft = () => {
    const raw = draft.trim();
    if (!raw) return;
    if (findDuplicate([...names, raw])) {
      setError(`“${raw}” is already listed.`);
      return;
    }
    setError(null);
    setNames((prev) => [...prev, raw]);
    setDraft('');
  };

  return (
    <WModal
      isOpen
      onClose={onClose}
      title="Add (P) names"
      modalClasses="tw-w-[520px] tw-max-w-[95vw]"
    >
      <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
        <p className={FIELD_HELP_CLASS}>
          These (P) names will be added to every selected recording. Names a
          recording already carries are left as they are.
        </p>
        {names.length > 0 ? (
          <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            {names.map((v) => (
              <ValueChip
                key={v}
                value={v}
                edited={false}
                changed
                onRemove={() => {
                  setNames((prev) => prev.filter((x) => x !== v));
                  if (error) setError(null);
                }}
              />
            ))}
          </div>
        ) : (
          <p className="tw-text-sm tw-text-textSeal tw-italic">None yet</p>
        )}
        <div className="tw-flex tw-flex-col tw-gap-2">
          <span className={FIELD_LABEL_CLASS}>(P) name</span>
          <div className="tw-flex tw-items-center tw-gap-2">
            <input
              className={FIELD_INPUT_CLASS}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addDraft();
                }
              }}
              placeholder="e.g. 1985 Parlophone Records Ltd"
            />
            <WButton
              variant="secondary"
              icon="plus"
              label="Add"
              disabled={draft.trim().length === 0}
              onClick={addDraft}
            />
          </div>
          {error && <p className={FIELD_ERROR_CLASS}>{error}</p>}
          {draft.trim().length > 0 && !error && (
            <p className="tw-text-yellow-300 tw-text-xs">
              You have an unadded (P) name. Click “Add” to include it before
              saving, or clear the field.
            </p>
          )}
        </div>
        <div className={MODAL_ACTIONS_CLASS}>
          <WButton variant="secondary" label="Cancel" onClick={onClose} />
          <WButton
            variant="primary"
            label="Save"
            // Block save while a draft is uncommitted so the typed value isn't
            // silently dropped, and require at least one name.
            disabled={names.length === 0 || draft.trim().length > 0}
            onClick={() => onSave(names)}
          />
        </div>
      </div>
    </WModal>
  );
};

/**
 * Year of Recording: four digits within the shared bounds, the same input
 * discipline as the edit form and mass edit. The recording-before-release rule
 * is not checked here — it depends on each recording's own release date, so
 * it lives in the builder's pre-warning panel instead.
 */
const YearValueEditor: React.FC<{
  initial: string;
  onClose: () => void;
  onSave: (year: string) => void;
}> = ({ initial, onClose, onSave }) => {
  const [value, setValue] = useState(initial);
  const [touched, setTouched] = useState(false);
  const trimmed = value.trim();
  const error =
    trimmed.length === 0
      ? 'A year is required'
      : validateRecordingYear(trimmed);
  const showError = touched && error !== null;

  return (
    <WModal
      isOpen
      onClose={onClose}
      title="Edit year of recording"
      modalClasses="tw-w-[520px] tw-max-w-[95vw]"
    >
      <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
        <p className={FIELD_HELP_CLASS}>
          This year of recording will be applied to the selected recordings. It
          can’t be later than the year of each recording’s earliest linked
          release — recordings that would break that rule are listed before you
          apply, and skipped by the backend.
        </p>
        <div className="tw-flex tw-flex-col tw-gap-2">
          <span className={FIELD_LABEL_CLASS}>Year of recording</span>
          <input
            className={clsx(
              FIELD_INPUT_CLASS,
              showError && FIELD_ERROR_BORDER_CLASS
            )}
            value={value}
            inputMode="numeric"
            placeholder="YYYY"
            maxLength={4}
            onChange={(e) => {
              setValue(e.target.value.replace(/\D/g, '').slice(0, 4));
              setTouched(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && error === null) {
                e.preventDefault();
                onSave(trimmed);
              }
            }}
          />
          {showError ? (
            <p className={FIELD_ERROR_CLASS}>{error}</p>
          ) : (
            <p className={FIELD_HINT_CLASS}>Four digits, e.g. 1985.</p>
          )}
        </div>
        <div className={MODAL_ACTIONS_CLASS}>
          <WButton variant="secondary" label="Cancel" onClick={onClose} />
          <WButton
            variant="primary"
            label="Save"
            disabled={error !== null}
            onClick={() => onSave(trimmed)}
          />
        </div>
      </div>
    </WModal>
  );
};

/** Country of Recording: the same ISO alpha-3 select as the edit form and
 *  mass edit — shows the name, stores the code. */
const CountryValueEditor: React.FC<{
  initial: string;
  onClose: () => void;
  onSave: (code: string) => void;
}> = ({ initial, onClose, onSave }) => {
  const [value, setValue] = useState(initial);
  return (
    <WModal
      isOpen
      onClose={onClose}
      title="Edit country of recording"
      modalClasses="tw-w-[520px] tw-max-w-[95vw]"
    >
      <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
        <p className={FIELD_HELP_CLASS}>
          This country of recording will be applied to the selected recordings.
        </p>
        <div className="tw-flex tw-flex-col tw-gap-2">
          <span className={FIELD_LABEL_CLASS}>Country of recording</span>
          <select
            className={fieldControlClass({
              kind: 'select',
              hasValue: !!value,
            })}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          >
            <option value="">Select country</option>
            {countryCodes.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
        <div className={MODAL_ACTIONS_CLASS}>
          <WButton variant="secondary" label="Cancel" onClick={onClose} />
          <WButton
            variant="primary"
            label="Save"
            disabled={value.trim().length === 0}
            onClick={() => onSave(value)}
          />
        </div>
      </div>
    </WModal>
  );
};

/** Checklist editor used by the delete operations. */
const DeleteListEditor: React.FC<{
  title: string;
  description: string;
  options: string[];
  initial: string[];
  onClose: () => void;
  onSave: (selected: string[]) => void;
}> = ({ title, description, options, initial, onClose, onSave }) => {
  const [selected, setSelected] = useState<string[]>(initial);
  // Dedupe (case-insensitive) so the same role/artist assigned across several
  // recordings shows once, avoiding duplicate React keys and repeated rows.
  const uniqueOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const o of options) {
      const key = o.toLowerCase();
      if (o && !seen.has(key)) {
        seen.add(key);
        out.push(o);
      }
    }
    return out;
  }, [options]);
  const toggle = (value: string) =>
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  return (
    <WModal
      isOpen
      onClose={onClose}
      title={title}
      modalClasses="tw-w-[520px] tw-max-w-[95vw]"
    >
      <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
        <p className={FIELD_HELP_CLASS}>{description}</p>
        <CheckList
          options={uniqueOptions}
          selected={selected}
          onToggle={toggle}
        />
        <div className={MODAL_ACTIONS_CLASS}>
          <WButton variant="secondary" label="Cancel" onClick={onClose} />
          <WButton
            variant="primary"
            label="Save"
            disabled={selected.length === 0}
            onClick={() => onSave(selected)}
          />
        </div>
      </div>
    </WModal>
  );
};

const CONTRIBUTION_EDITOR_COPY: Record<
  'Add' | 'Edit' | 'Replace',
  { title: string; help: string; pickLabel: string; pickPlaceholder: string }
> = {
  Add: {
    title: 'Add contributions',
    help: 'Add one or more contributions to the selected recordings. Click a contribution to change its data quality or evidence.',
    pickLabel: 'Add a contribution',
    pickPlaceholder: 'Search or select contribution…',
  },
  Edit: {
    title: 'Edit contributions',
    help: 'Change the data quality, evidence or alias of contributions the selected recordings already have. Only contributions currently assigned across the selection can be edited — nothing is added.',
    pickLabel: 'Choose a contribution to edit',
    pickPlaceholder: 'Search assigned contributions…',
  },
  Replace: {
    title: 'Replace all contributions',
    help: 'Define the full set of contributions. These will replace every contribution currently set on the selected recordings.',
    pickLabel: 'Add a contribution',
    pickPlaceholder: 'Search or select contribution…',
  },
};

/**
 * Add / Edit / Replace-all editor: build a list of contributions, each
 * configured via the reused ContributionDetailModal.
 *
 * The three operations share one surface because they build the same list —
 * only the wire `op` and where the roles come from differ. Edit offers the
 * roles actually assigned across the selection (there is nothing else to edit).
 *
 * All three demand an alias. Edit used to be exempt on the grounds that it
 * targets a contribution that already exists, but the backend rewrites the
 * contribution edge with `source_id = persona_id` on an edit too, so an
 * alias-less edit wrote an edge with an empty source instead of failing.
 *
 * Unlike the single-recording surfaces, there is no alias to seed here: a bulk
 * plan spans a whole selection, whose recordings need not credit the same
 * persona. The user picks explicitly.
 */
const ContributionListEditor: React.FC<{
  operation: 'Add' | 'Edit' | 'Replace';
  contributionOptions: string[];
  /** Roles currently assigned across the selection — the only editable ones. */
  assignedContributions: string[];
  availableAliases: ContributionAliasOption[];
  initial: ConfiguredContribution[];
  onClose: () => void;
  onSave: (entries: ConfiguredContribution[]) => void;
}> = ({
  operation,
  contributionOptions,
  assignedContributions,
  availableAliases,
  initial,
  onClose,
  onSave,
}) => {
  const [entries, setEntries] = useState<ConfiguredContribution[]>(initial);
  const [pendingRole, setPendingRole] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const isEditOp = operation === 'Edit';
  const copy = CONTRIBUTION_EDITOR_COPY[operation];
  const detailOpen = pendingRole !== null || editingIndex !== null;
  const editingEntry = editingIndex !== null ? entries[editingIndex] : null;
  const editingNotes = parseEvidenceNotes(editingEntry?.confidencePattern);

  // Dedupe case-insensitively: the same role assigned across several recordings
  // arrives once per recording.
  const roleOptions = useMemo(() => {
    const source = isEditOp ? assignedContributions : contributionOptions;
    return Array.from(
      new Map(source.filter(Boolean).map((r) => [r.toLowerCase(), r])).values()
    ).sort();
  }, [isEditOp, assignedContributions, contributionOptions]);

  return (
    <>
      <WModal
        isOpen={!detailOpen}
        onClose={onClose}
        title={copy.title}
        modalClasses="tw-w-[560px] tw-max-w-[95vw]"
      >
        <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
          <p className={FIELD_HELP_CLASS}>{copy.help}</p>
          {entries.length > 0 && (
            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
              {/* The same chip the recording edit form and the mass-edit modals
                  render, so a contribution's data-quality colour means the same
                  thing on every surface. */}
              {entries.map((c, index) => (
                <ContributionChip
                  key={c.contribution}
                  role={c.contribution}
                  confidenceCategory={c.confidenceCategory}
                  edited={false}
                  onSelect={() => setEditingIndex(index)}
                  onRemove={() =>
                    setEntries((prev) => prev.filter((_, i) => i !== index))
                  }
                />
              ))}
            </div>
          )}
          {roleOptions.length === 0 && isEditOp ? (
            <p className={FIELD_HELP_CLASS}>
              None of the selected recordings have any contributions to edit.
            </p>
          ) : (
            <div className="tw-flex tw-flex-col tw-gap-2">
              <span className={FIELD_LABEL_CLASS}>{copy.pickLabel}</span>
              <RoleSelect
                options={roleOptions}
                exclude={entries.map((e) => e.contribution)}
                onSelect={(role) => setPendingRole(role)}
                placeholder={copy.pickPlaceholder}
              />
            </div>
          )}
          <div className={MODAL_ACTIONS_CLASS}>
            <WButton variant="secondary" label="Cancel" onClick={onClose} />
            <WButton
              variant="primary"
              label="Save"
              disabled={entries.length === 0}
              onClick={() => onSave(entries)}
            />
          </div>
        </div>
      </WModal>

      {/* Reused contribution detail modal for the role being added or amended. */}
      <ContributionDetailModal
        isOpen={detailOpen}
        // Both modes require an alias; `mode` only picks the wording. An
        // Edit-operation row never creates a contribution, and neither does
        // reopening a chip, so both go in as 'edit'.
        mode={isEditOp || editingIndex !== null ? 'edit' : 'add'}
        initialValue={
          editingEntry
            ? {
                contributionName: editingEntry.contribution,
                confidenceCategory: editingEntry.confidenceCategory ?? '1',
                evidenceUrl: editingNotes.evidenceUrl,
                notes: editingNotes.notes,
                personaId: editingEntry.personaId ?? '',
              }
            : { contributionName: pendingRole ?? '' }
        }
        aliases={availableAliases}
        onClose={() => {
          setPendingRole(null);
          setEditingIndex(null);
        }}
        onSave={(payload) => {
          setEntries((prev) =>
            editingIndex !== null
              ? prev.map((e, i) => (i === editingIndex ? payload : e))
              : [...prev, payload]
          );
          setPendingRole(null);
          setEditingIndex(null);
        }}
      />
    </>
  );
};

const featuredLabel = (status: FeaturedStatus): string =>
  status === 'fa'
    ? 'Featured'
    : status === 'ofa'
    ? 'Other Featured'
    : 'Non-Featured';

/** Artist add/replace — builds a list of artists, each added either by
 *  searching existing artists (carrying identity + featured status) or via the
 *  reused "Create new artist" modal. Mirrors the recording edit form so links
 *  carry an id/members and a Featured / Other Featured choice. */
const ArtistAddEditor: React.FC<{
  replaceAll: boolean;
  availableArtists: string[];
  userId?: string;
  parentRecordingId?: string;
  initial: ArtistEntry[];
  onClose: () => void;
  onSave: (entries: ArtistEntry[]) => void;
}> = ({ replaceAll, userId, parentRecordingId, initial, onClose, onSave }) => {
  const [entries, setEntries] = useState<ArtistEntry[]>(initial);
  const [addingArtist, setAddingArtist] = useState(false);
  // An existing artist picked from search, awaiting the featured-status choice.
  const [pendingArtist, setPendingArtist] = useState<SelectedArtist | null>(
    null
  );

  return (
    <>
      <WModal
        isOpen={!addingArtist && pendingArtist === null}
        onClose={onClose}
        title={replaceAll ? 'Replace all artists' : 'Add artists'}
        modalClasses="tw-w-[560px] tw-max-w-[95vw]"
      >
        <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
          <p className={FIELD_HELP_CLASS}>
            {replaceAll
              ? 'Define the full set of artists. These will replace every artist currently set on the selected recordings.'
              : 'Add one or more artists to the selected recordings.'}
          </p>
          {entries.length > 0 && (
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {entries.map((a, index) => (
                <span
                  key={`${a.name}-${index}`}
                  className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-surfaceBlack80 tw-border tw-border-formInputBackground tw-px-3 tw-py-1 tw-text-xs tw-text-white"
                >
                  {a.name}
                  <span className="tw-text-textSeal">
                    · {featuredLabel(a.featuredStatus)}
                  </span>
                  {a.pendingCreate && (
                    <span className="tw-text-textLime">· new</span>
                  )}
                  <button
                    type="button"
                    aria-label={`Remove ${a.name}`}
                    className="tw-text-textDove hover:tw-text-white"
                    onClick={() =>
                      setEntries((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    <X className="tw-w-3 tw-h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {/* Search & add an existing artist (real lookup, carries id/members).
              Selecting one opens the featured-status modal, as on the edit
              form — the featured choice is a second step, not an inline toggle. */}
          <div className="tw-flex tw-flex-col tw-gap-2">
            <span className={FIELD_LABEL_CLASS}>Search existing artists</span>
            <ArtistSearchDropdown
              userId={userId}
              parentRecordingId={parentRecordingId}
              existing={entries}
              onSelect={(artist) => setPendingArtist(artist)}
            />
          </div>
          {/* …or create a brand-new artist (same flow as the edit form) */}
          <div className="tw-flex tw-justify-start">
            <WButton
              variant="secondary"
              icon="plus"
              label="Create new artist"
              onClick={() => setAddingArtist(true)}
            />
          </div>
          <div className={MODAL_ACTIONS_CLASS}>
            <WButton variant="secondary" label="Cancel" onClick={onClose} />
            <WButton
              variant="primary"
              label="Save"
              disabled={entries.length === 0}
              onClick={() => onSave(entries)}
            />
          </div>
        </div>
      </WModal>

      {/* Featured-status choice for a just-searched artist (edit-form parity:
          search first, then pick Featured / Other Featured in a second modal). */}
      <FeaturedStatusModal
        pending={pendingArtist}
        onCancel={() => setPendingArtist(null)}
        onAdd={(entry) => {
          setEntries((prev) =>
            prev.some((e) => e.name === entry.name || e.id === entry.id)
              ? prev
              : [...prev, entry]
          );
          setPendingArtist(null);
        }}
      />

      <ArtistEditorModal
        isOpen={addingArtist}
        userId={userId}
        parentRecordingId={parentRecordingId}
        onClose={() => setAddingArtist(false)}
        onAdd={(entry) => {
          setEntries((prev) => [...prev, entry]);
          setAddingArtist(false);
        }}
      />
    </>
  );
};

export default BulkEditModal;
